import React, { useState } from 'react';
import { RiAddLine, RiArrowDownSLine, RiLoader4Line } from 'react-icons/ri';
import { CasesApi } from '@/api';
import { CriteriaAnalysis } from '@/types/document';
import Popover from '@/components/ui/Popover';

interface CriteriaPickerProps {
  caseId: string;
  documentId: string;
  assignedCriteriaIds: string[];
  availableCriteria: CriteriaAnalysis[];
  onAssignSuccess: () => void;
}

const CriteriaPicker: React.FC<CriteriaPickerProps> = ({
  caseId,
  documentId,
  assignedCriteriaIds,
  availableCriteria,
  onAssignSuccess,
}) => {
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleCriterion = async (criteriaId: string) => {
    if (assigningId) return; // Prevent multiple concurrent assignments

    setAssigningId(criteriaId);
    try {
      await CasesApi.assignDocumentToCriteria(caseId, documentId, criteriaId);
      onAssignSuccess();
    } catch (error) {
      console.error('Failed to assign criterion:', error);
    } finally {
      setAssigningId(null);
    }
  };

  // Filter out already assigned criteria
  const unassignedCriteria = availableCriteria.filter(
    c => !assignedCriteriaIds.includes(c.criteriaId)
  );

  const popoverContent = (
    <div className="w-80 max-h-80 overflow-auto py-1">
      {unassignedCriteria.length === 0 ? (
        <div className="px-3 py-2 text-gray-500 text-xs">
          All criteria assigned
        </div>
      ) : (
        <div className="py-1">
          {unassignedCriteria.map(criterion => {
            const isAssigning = assigningId === criterion.criteriaId;
            return (
              <button
                key={criterion.criteriaId}
                type="button"
                onClick={() => handleToggleCriterion(criterion.criteriaId)}
                disabled={!!assigningId}
                className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-purple-50 cursor-pointer text-left ${
                  assigningId && !isAssigning ? 'opacity-50' : ''
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  {isAssigning ? (
                    <RiLoader4Line className="w-4 h-4 text-purple-600 animate-spin" />
                  ) : (
                    <div className="w-4 h-4 rounded border-2 border-gray-300 hover:border-purple-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900">
                    {criterion.criteriaName}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      open={isOpen}
      onOpenChange={setIsOpen}
      side="bottom"
      align="start"
      trigger="click"
    >
      <button
        type="button"
        className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200 transition-colors disabled:opacity-50"
        disabled={!!assigningId}
      >
        {assigningId ? (
          <RiLoader4Line className="w-3 h-3 animate-spin" />
        ) : (
          <RiAddLine className="w-3 h-3" />
        )}
        <span>{assigningId ? 'Assigning...' : 'Select Criteria'}</span>
        <RiArrowDownSLine
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
    </Popover>
  );
};

export default CriteriaPicker;
